import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { reportDistribution, reportNumber, reportSum } from '../../utils/rivalReport';

const SCOPES = [
  { label: 'stats.total', suffix: 'Total', wins: 'ganados', losses: 'perdidos' },
  { label: 'stats.local', suffix: 'Local', wins: 'ganadosCasa', losses: 'perdidosCasa' },
  { label: 'stats.visitante', suffix: 'Visitante', wins: 'ganadosFuera', losses: 'perdidosFuera' },
];
const RESPONSE = [
  ['comebacks', 'remontadas'],
  ['recoveredPoints', 'puntosRecuperados'],
  ['lostPoints', 'puntosPerdidos'],
  ['scoredFirst', 'partidosEmpiezaGanando'],
  ['concededFirst', 'partidosEmpiezaPerdiendo'],
];

function GlobalPanel({ title, eyebrow, help, icon: Icon, children, className = '' }) {
  const titleId = useId();
  const { t } = useTranslation('reports');
  return <section className={`rr-panel rr-global-panel ${className}`} aria-labelledby={titleId}>
    <header className="rr-performance-heading">
      <div><span className="rr-eyebrow">{t(`modern.global.${eyebrow}`)}</span>
        <h3 id={titleId}>{t(`modern.global.${title}`)}</h3></div><Icon aria-hidden="true" />
    </header>
    {help && <p className="rr-performance-help">{t(`modern.global.${help}`)}</p>}
    {children}
  </section>;
}

function ScopeDistribution({ label, values, labels, expected, minutes = false, missingValue = '—' }) {
  const { t, i18n } = useTranslation('reports');
  const { counts, shares } = reportDistribution(values, expected ?? reportSum(...values));
  const total = reportSum(...counts);
  const format = value => value === null ? missingValue : value.toLocaleString(i18n.language, { maximumFractionDigits: 1 });
  return <section className="rr-global-scope" aria-label={t(label)}>
    <header><h4>{t(label)}</h4><span>{t(`modern.global.${minutes ? 'recordedMinutes' : 'recordedResults'}`, { value: format(total) })}</span></header>
    {shares && <div className="rr-outcome-track rr-global-track" aria-hidden="true">
      {shares.map((share, index) => <span className={`rr-series-${index}`} key={index} style={{ width: `${share * 100}%` }} />)}
    </div>}
    <dl className="rr-global-values">
      {counts.map((value, index) => <div key={labels[index]} className={`rr-series-${index}`}>
        <dt><i aria-hidden="true" />{t(`modern.global.${labels[index]}`)}</dt>
        <dd aria-label={t(`modern.global.${labels[index]}`)}>{format(value)}
          {minutes && <span className="rr-value-unit">{t('modern.global.minutes')}</span>}
          {shares && <small>{shares[index].toLocaleString(i18n.language, { style: 'percent', maximumFractionDigits: 1 })}</small>}
        </dd>
      </div>)}
    </dl>
    {!shares && <p className="rr-distribution-note">{t(total === 0
      ? 'modern.global.noRecords' : 'modern.breakdownUnavailable')}</p>}
  </section>;
}

export default function ReportGlobalStatistics({ estadisticas, clasificacion, missingValue = '—' }) {
  const { t, i18n } = useTranslation('reports');
  const titleId = useId();
  const data = estadisticas?.datos_extra;
  if (!data) return <p className="rr-empty">{t('modern.global.unavailable')}</p>;
  const sameSample = reportNumber(clasificacion?.jugados) !== null
    && reportNumber(estadisticas?.partidos?.partidosJugados) === reportNumber(clasificacion?.jugados);
  const format = value => reportNumber(value)?.toLocaleString(i18n.language, { maximumFractionDigits: 1 }) ?? missingValue;
  return <section className="rr-global-report" aria-labelledby={titleId}>
    <header className="rr-global-heading"><span className="rr-eyebrow">{t('modern.global.eyebrow')}</span>
      <h2 id={titleId}>{t('sections.globalStatsExtended')}</h2><p>{t('modern.global.subtitle')}</p></header>

    <GlobalPanel title="scoreStates" eyebrow="scoreEyebrow" help="minutesHelp" icon={AccessTimeOutlinedIcon} className="rr-global-time">
      <div className="rr-global-scopes">
        {SCOPES.map(scope => <ScopeDistribution key={scope.suffix} label={scope.label} minutes missingValue={missingValue}
          labels={['minutesLeading', 'minutesLevel', 'minutesTrailing']}
          values={['minutosGanando', 'minutosEmpatando', 'minutosPerdiendo'].map(key => data[`${key}${scope.suffix}`])} />)}
      </div>
    </GlobalPanel>

    <div className="rr-global-margins">
      {[
        { title: 'winningMargins', prefix: 'victoria', expected: 'wins', icon: TrendingUpIcon },
        { title: 'losingMargins', prefix: 'derrota', expected: 'losses', icon: TrendingDownIcon },
      ].map(group => <GlobalPanel key={group.prefix} title={group.title} eyebrow="marginsEyebrow"
        help="marginsHelp" icon={group.icon} className={`rr-margin-${group.prefix}`}>
        <div className="rr-global-margin-scopes">
          {SCOPES.map(scope => <ScopeDistribution key={scope.suffix} label={scope.label} missingValue={missingValue}
            labels={['oneGoal', 'twoGoals', 'threeGoals']}
            expected={sameSample ? reportNumber(clasificacion[scope[group.expected]]) : null}
            values={['Por1Gol', 'Por2Goles', 'Por3OMasGoles'].map(key => data[`${group.prefix}${key}${scope.suffix}`])} />)}
        </div>
      </GlobalPanel>)}
    </div>

    <GlobalPanel title="response" eyebrow="responseEyebrow" icon={SyncAltIcon}>
      <dl className="rr-global-points">
        {RESPONSE.slice(1, 3).map(([label, key], index) => <div className={`rr-tone-${index === 0 ? 'win' : 'loss'}`} key={key}>
          <dt>{t(`modern.global.${label}`)}</dt><dd>{format(data[`${key}Total`])}<small>{t('stats.total')}</small></dd>
        </div>)}
      </dl>
      <div className="rr-data-table rr-global-response-table"><table aria-label={t('modern.global.response')}>
        <thead><tr><th scope="col">{t('modern.metric')}</th>{SCOPES.map(scope =>
          <th scope="col" key={scope.suffix}>{t(scope.label)}</th>)}</tr></thead>
        <tbody>{RESPONSE.map(([label, key]) => <tr key={key}><th scope="row">{t(`modern.global.${label}`)}</th>
          {SCOPES.map(scope => <td key={scope.suffix}>{format(data[`${key}${scope.suffix}`])}</td>)}</tr>)}</tbody>
      </table></div>
    </GlobalPanel>

    <GlobalPanel title="playersBalance" eyebrow="playersEyebrow" help="playersHelp" icon={GroupsOutlinedIcon}>
      <div className="rr-global-player-counts">
        {[
          ['superiority', 'minutosSuperioridad', 'win'],
          ['inferiority', 'minutosInferioridad', 'loss'],
        ].map(([label, prefix, tone]) => <section className={`rr-global-player-card rr-tone-${tone}`} key={prefix}
          aria-label={t(`modern.global.${label}`)}>
          <h4>{t(`modern.global.${label}`)}</h4>
          <p className="rr-player-minutes">{format(data[`${prefix}Total`])}<span>{t('modern.global.minutes')}</span></p>
          <dl>{SCOPES.slice(1).map(scope => <div key={scope.suffix}><dt>{t(scope.label)}</dt>
            <dd aria-label={t(scope.label)}>{format(data[`${prefix}${scope.suffix}`])}</dd></div>)}</dl>
        </section>)}
      </div>
    </GlobalPanel>
  </section>;
}
